import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Avatar } from "./Avatar";
import { AudioBar } from "./AudioBar";
import { useSiteImages } from "../context/SiteImagesContext";
import {
  Home, Search, PlusCircle, MessageCircle, Bell, User, Music2, LogOut, Shield, LayoutDashboard,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "./ui/dropdown-menu";

function Logo() {
  const { img } = useSiteImages();
  return (
    <Link to="/" className="flex items-center gap-2 group" data-testid="app-logo">
      <img src={img("logo")} alt="ilovemuzik" className="w-9 h-9 rounded-xl object-contain bg-black glow-violet" />
      <span className="font-display font-extrabold text-lg tracking-tight">
        ILOVE<span className="gradient-text">MUZIK</span>
      </span>
    </Link>
  );
}

export function AppShell({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let active = true;
    const load = () => api.get("/notifications/unread-count").then((r) => active && setUnread(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { active = false; clearInterval(t); };
  }, [user, loc.pathname]);

  const isArtist = user?.role === "artist";
  const isAdmin = user?.role === "admin";

  const navItems = isArtist
    ? [
        { to: "/app", icon: LayoutDashboard, label: "Tableau" },
        { to: "/app/create", icon: PlusCircle, label: "Recherche" },
        { to: "/app/messages", icon: MessageCircle, label: "Messages" },
        { to: "/app/notifications", icon: Bell, label: "Notifs", badge: unread },
        { to: "/app/profile", icon: User, label: "Profil" },
      ]
    : [
        { to: "/app/feed", icon: Search, label: "Recherches" },
        { to: "/app/productions", icon: Music2, label: "Mes prods" },
        { to: "/app/messages", icon: MessageCircle, label: "Messages" },
        { to: "/app/notifications", icon: Bell, label: "Notifs", badge: unread },
        { to: "/app/profile", icon: User, label: "Profil" },
      ];

  return (
    <div className="min-h-screen pb-24 sm:pb-0">
      <header className="sticky top-0 z-30 glass border-b border-zinc-800/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-1 sm:gap-3">
            <Link to="/app/notifications" className="relative p-2 text-zinc-300 hover:text-white" data-testid="topbar-notifications">
              <Bell size={20} />
              {unread > 0 && (
                <span className="absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-violet-500 text-[10px] font-bold flex items-center justify-center text-white">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="outline-none">
                <Avatar user={user} size={34} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="glass border-zinc-800 w-52">
                <div className="px-2 py-2">
                  <p className="font-semibold text-sm">@{user?.pseudo}</p>
                  <p className="text-xs text-zinc-400 capitalize">{user?.role}</p>
                </div>
                <DropdownMenuSeparator />
                {!isAdmin && (
                  <>
                    <DropdownMenuItem onClick={() => nav(isArtist ? "/app" : "/app/feed")} data-testid="menu-dashboard">
                      <Home size={16} className="mr-2" /> {isArtist ? "Mon tableau" : "Les recherches"}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => nav("/app/profile")} data-testid="menu-profile">
                      <User size={16} className="mr-2" /> Mon profil
                    </DropdownMenuItem>
                  </>
                )}
                {isAdmin && (
                  <DropdownMenuItem onClick={() => nav("/app/admin")} data-testid="menu-admin">
                    <Shield size={16} className="mr-2" /> Administration
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); nav("/"); }} data-testid="menu-logout" className="text-red-400">
                  <LogOut size={16} className="mr-2" /> Déconnexion
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {!isAdmin && (
        <div className="hidden sm:block sticky top-14 z-20 glass border-b border-zinc-800/50">
          <div className="max-w-7xl mx-auto px-6 flex gap-1 h-11 items-center">
            {navItems.map((item) => {
              const active = loc.pathname === item.to;
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to} data-testid={`nav-desktop-${item.label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-violet-600/20 text-violet-300" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"}`}>
                  <Icon size={16} /> {item.label}
                  {item.badge > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-violet-500 text-[10px] font-bold flex items-center justify-center text-white">{item.badge}</span>}
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {children}
      </main>

      {!isAdmin && (
        <nav className="fixed bottom-0 left-0 right-0 z-40 glass border-t border-zinc-800/70 sm:hidden" data-testid="bottom-nav">
          <div className="grid grid-cols-5 h-16">
            {navItems.map((item) => {
              const active = loc.pathname === item.to;
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase()}`}
                  className={`flex flex-col items-center justify-center gap-0.5 relative transition-colors ${active ? "text-violet-400" : "text-zinc-500"}`}>
                  <div className="relative">
                    <Icon size={22} />
                    {item.badge > 0 && (
                      <span className="absolute -top-1 -right-2 min-w-[15px] h-[15px] px-1 rounded-full bg-violet-500 text-[9px] font-bold flex items-center justify-center text-white">
                        {item.badge > 9 ? "9+" : item.badge}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-medium">{item.label}</span>
                </Link>
              );
            })}
          </div>
        </nav>
      )}

      {/* Desktop side actions row */}
      {!isAdmin && (
        <div className="hidden">
          <div className="max-w-7xl mx-auto px-6 flex gap-1 h-11 items-center w-full">
            {navItems.map((item) => {
              const active = loc.pathname === item.to;
              const Icon = item.icon;
              return (
                <Link key={item.to} to={item.to}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${active ? "bg-violet-600/20 text-violet-300" : "text-zinc-400 hover:text-white hover:bg-zinc-800/60"}`}>
                  <Icon size={16} /> {item.label}
                  {item.badge > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-violet-500 text-[10px] font-bold flex items-center justify-center text-white">{item.badge}</span>}
                </Link>
              );
            })}
          </div>
        </div>
      )}
      {!isAdmin && <div className="hidden" />}

      <AudioBar />
    </div>
  );
}
